import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { Merchant, OrderPreparation, RiderApi } from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  EmptyRow,
  Group,
  MarketShell,
  Totals,
  type BadgeValue,
  type StepsValue,
  type TotalValue,
} from "./MarketShell.tsx";
import { Absent } from "./PhoneShell.tsx";
import { MarketState } from "./TrancheState.tsx";

/**
 * `Order tracking` — COM-07's preparation, and the three things around it that have no source.
 *
 * | Drawn                                   | From                                                |
 * | --------------------------------------- | --------------------------------------------------- |
 * | `Packing your order`                    | COM-07's `orderPreparation.status`                  |
 * | `preparing`                             | the same field, in the chip                         |
 * | `Accepted 12:41`                        | COM-07's `acceptedAt`                               |
 * | `ready about 12:55`                     | COM-07's `readyAt`, once the store has said so      |
 * | the four-step bar                       | the same `status`, mapped to its first three steps  |
 * | `Sukuma wiki · 2 → 1 bunch`             | **nothing** — no substitutions are published        |
 * | `Refund KES 30 · to M-Pesa`             | **nothing reachable** — see below                   |
 * | `Courier · assigned when ready`         | the design's own copy while there is no courier     |
 * | the store's progress arriving unasked   | **nothing** — RTM-001, and this screen polls nothing |
 *
 * Three of those are the measurement's for this screen — the substitutions, the unasked progress,
 * and the refund row by extension. Each is drawn as an absence in its designed place:
 *
 *   - **The substitutions are the group this screen exists for.** `Changes from the store` is a
 *     heading over one row saying what the store swapped, and neither COM-06's acceptance nor
 *     COM-07's preparation publishes a substitution. So the group is drawn with its heading and one
 *     row carrying the reason, rather than the heading being dropped — a rider who is told nothing
 *     changed and then receives one bunch instead of two has been told a falsehood by omission.
 *   - **The refund is published and unreachable from here.** FIN-04 answers a refund by
 *     `refundId` and a listing filtered by `paymentIntentId`, and **COM-07 publishes neither** — its
 *     preparation carries an `orderId`, a `merchantId` and a `merchantLocationId`. So nothing joins
 *     an order to the money taken for it, which is the same shape of gap as R-28's ride-to-trip
 *     join. The `Refund` screen is reached by an address naming its own identifier for exactly this
 *     reason.
 *   - **Nothing pushes.** RTM-001: there is no transport that delivers order preparation progress
 *     unasked to this application. This screen therefore **does not poll** either. A one-second poll
 *     would look like a live screen and would be a worse lie than a static one — the reconciliation
 *     records the same judgement for `driver · 2 Online`, where polling widens a race the design
 *     already has a screen for. So the screen reads once, says what it read and when, and offers the
 *     read again.
 */

export interface OrderTrackingProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly orderId: string | undefined;
  readonly onBack: () => void;
}

/**
 * The four step labels, which the design hard-codes **outside** its own `SC` table.
 *
 * `['Accepted','Preparing','Ready','On the way']` lives in `renderVals` rather than in either
 * screen's data, so both screens that draw a step bar draw these four words. On this screen that is
 * right. On `Refund` it is not, and `Refund.tsx` records it.
 *
 * The fourth step has no source: `On the way` is a delivery, and DEL-06's tracking is addressed by a
 * `deliveryId` nothing joins to a commerce order. So the bar never reaches its fourth segment, which
 * is drawn in the hairline colour as the design draws an unreached step.
 */
const STEP_LABELS: readonly [string, string, string, string] = [
  "Accepted",
  "Preparing",
  "Ready",
  "On the way",
];

/** COM-07's three statuses against the step bar's first three segments. Total, not defaulted. */
const STEP_FOR: Readonly<Record<OrderPreparation["status"], number>> = {
  pending: 0,
  preparing: 1,
  ready: 2,
};

/** The same three against the chip's kinds. */
const KIND_FOR: Readonly<Record<OrderPreparation["status"], BadgeValue["kind"]>> = {
  pending: "idle",
  preparing: "active",
  ready: "done",
};

/**
 * The card's title per status.
 *
 * The design draws one of the three — `preparing`, as `Packing your order`. The other two are states
 * this screen reaches and the design is silent about, so the words are this build's and are flagged
 * as such on screen rather than passing as approved copy.
 */
const TITLE_FOR: Readonly<Record<OrderPreparation["status"], string>> = {
  pending: "The store has your order",
  preparing: "Packing your order",
  ready: "Your order is ready",
};

export function OrderTracking(props: OrderTrackingProps): ReactNode {
  const { api, orderId } = props;

  const [preparation, setPreparation] = useState<OrderPreparation | undefined>(undefined);
  const [merchant, setMerchant] = useState<Merchant | undefined>(undefined);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(() => {
    if (orderId === undefined) return;
    setState({ kind: "loading" });
    api
      .orderPreparation(orderId)
      .then((answer) => {
        setPreparation(answer);
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => setState(stateForError(error, [SCOPES.orderPreparation])));
  }, [api, orderId]);

  useEffect(load, [load]);

  useEffect(() => {
    const merchantId = preparation?.merchantId;
    if (merchantId === undefined) return;
    let current = true;
    api
      .merchant(merchantId)
      .then((answer) => {
        if (current) setMerchant(answer);
      })
      .catch(() => {
        if (current) setMerchant(undefined);
      });
    return (): void => {
      current = false;
    };
  }, [api, preparation?.merchantId]);

  if (orderId === undefined) {
    return (
      <MarketState
        state={{
          kind: "error",
          code: "no_order_in_address",
          message:
            "This screen is about one order and the address does not name one. COM-05 publishes a POST and a read by orderId, and no listing, so nothing can find it.",
          retryable: false,
          conflict: false,
        }}
        onBack={props.onBack}
        purpose="Reading how your order is going."
        barTitle="Order"
        barSub="CityCab"
      />
    );
  }

  if (state.kind !== "ready" || preparation === undefined) {
    return (
      <MarketState
        state={state}
        onRetry={load}
        onBack={props.onBack}
        purpose="Reading how your order is going."
        barTitle="Order"
        barSub="CityCab"
      />
    );
  }

  const steps: StepsValue = { labels: STEP_LABELS, current: STEP_FOR[preparation.status] };

  const totals: TotalValue[] = [
    {
      label: "Refund",
      absent:
        "No refund: COM-07 publishes no paymentIntentId, and FIN-04 answers by refundId or that filter",
    },
    {
      label: "Courier",
      /*
       * `assigned when ready` is the design's own copy for "there is not one yet", so it is drawn
       * verbatim while the store has not finished. Once the order **is** ready a courier should be
       * named, and nothing joins a commerce order to a DEL-06 delivery — so from that moment the row
       * carries the absence instead of a sentence that has stopped being true.
       */
      ...(preparation.status === "ready"
        ? {
            absent:
              "No courier: nothing joins a commerce order to a delivery, so DEL-06's tracking is not addressable",
          }
        : { value: "assigned when ready" }),
    },
  ];

  return (
    <MarketShell
      screen="order-tracking"
      left={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      barTitle={`Order ${orderId}`}
      barSub={
        merchant === undefined ? (
          <Absent words="No shop name: COM-07 publishes merchantId and not a name" />
        ) : (
          merchant.tradingName
        )
      }
      right={{
        glyph: "support_agent",
        label: "Support",
        unavailable:
          "SUP-01 publishes only /internal/v1 operations, which the gateway does not expose",
      }}
      badge={{ kind: KIND_FOR[preparation.status], label: preparation.status }}
      title={TITLE_FOR[preparation.status]}
      sub={acceptedLine(preparation)}
      {...(preparation.status === "preparing"
        ? {}
        : {
            flag: "`The store has your order` and `Your order is ready` are this build's wording",
          })}
      steps={steps}
      actions={[
        {
          label: "Message the store",
          glyph: "chat",
          primary: false,
          unavailable:
            "Nothing on this platform publishes a message thread between a customer and a merchant",
        },
      ]}
    >
      <Group
        heading="Changes from the store"
        rows={[]}
        empty={
          <EmptyRow words="No substitutions: neither COM-06's acceptance nor COM-07's preparation publishes what the store changed" />
        }
      />
      <Totals rows={totals} />
    </MarketShell>
  );
}

/**
 * `Accepted 12:41 · ready about 12:55` — two facts, and only the first is always published.
 *
 * COM-07 requires `acceptedAt` and makes `readyAt` optional, which is correct: a store that has not
 * finished has no instant to publish. So the second half of the line is the absence until it does,
 * rather than the whole line disappearing or an estimate being computed from a preparation time this
 * screen does not hold.
 */
function acceptedLine(preparation: OrderPreparation): ReactNode {
  const accepted = clock(preparation.acceptedAt);
  if (preparation.readyAt === undefined) {
    return (
      <>
        {`Accepted ${accepted} · `}
        <Absent words="No ready time until the store publishes one" />
      </>
    );
  }
  return `Accepted ${accepted} · ready about ${clock(preparation.readyAt)}`;
}

/** `12:41`, on this device's clock, which is the only clock a phone has. */
function clock(at: string): string {
  return new Date(at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}
